import * as ukey from "./signConfig/ESASignClient";
import { getUkeyStatus, getSystemTime } from "../api/home";
import { reqCompanyAuthDetail, queryUserOperationLog } from "../api/hy";
import { Message } from "element-ui";
import moment from "moment";

/** 定义状态常量 */
export const UKEY_STATUS = {
  SUCCESS: 1, // Ukey验证成功
  NO_NEED: 2, // 当前用户无需使用Ukey
  FAILED: 0, // Ukey验证失败
};

/**
 * Ukey 证书校验类
 * 封装所有 Ukey 相关操作，避免全局变量污染
 */
class UkeyValidator {
  constructor() {
    this.enterpriseId = "";
    this.certBase64 = "";
  }

  /**
   * 主入口：验证 Ukey 信息
   * @returns {Promise<number>} - 返回验证结果状态码
   */
  async verifyUkeyinfo(id) {
    try {
      this.enterpriseId = id;

      const res = await getUkeyStatus(this.enterpriseId);

      if (res?.data === 0) {
        // 需要校验 Ukey，等待验证结果
        return await new Promise((resolve, reject) => {
          ukey.ESACheckIsKey(this.getUkeyCert.bind(this, resolve, reject), null, false);
        });
      }

      if (res?.data === 1) {
        // 不需要校验 Ukey，返回特殊状态
        return UKEY_STATUS.NO_NEED;
      }

      // 其他状态码
      Message.error("获取Ukey状态失败，请稍后再试");
      return UKEY_STATUS.FAILED;
    } catch (error) {
      return UKEY_STATUS.FAILED;
    }
  }

  /**
   * 获取证书信息
   */
  async getUkeyCert(resolve, reject, status, msg, retpar, params) {
    try {
      if (status === 0) {
        // 已经插入 Ukey，获取证书信息
        ukey.BaseGetCert(1, this.getUkeyInfoSub.bind(this, resolve, reject), null, false);
        resolve(UKEY_STATUS.SUCCESS);
        return;
      } else {
        // 未检测到 Ukey
        Message.error(
          "未能连接上印章卡，请确认印章卡是否插入！若未下载/安装请先下载并安装后再插入签章（Ukey）",
        );
        reject(UKEY_STATUS.FAILED);
      }
    } catch (error) {
      reject(UKEY_STATUS.FAILED);
    }
  }

  /**
   * 获取证书 Base64 编码及有效期
   */
  async getUkeyInfoSub(resolve, reject, status, msg, retpar, params) {
    try {
      if (status === 0) {
        this.certBase64 = retpar[0];
        ukey.BaseGetCertSub(
          retpar[0],
          3,
          this.getCertValidPer.bind(this, resolve, reject),
          null,
          false,
        );
      } else {
        // Message.error("获取证书信息失败！");
        resolve(UKEY_STATUS.SUCCESS);
      }
    } catch (error) {
      resolve(UKEY_STATUS.SUCCESS);
    }
  }

  /**
   * 获取证书有效期
   */
  async getCertValidPer(resolve, reject, status, msg, retpar, params) {
    try {
      if (status !== 0) {
        // Message.error("获取证书有效期失败！");
        resolve(UKEY_STATUS.SUCCESS);
        return;
      }

      // 解码有效期
      const result = this.gbkBase64ToUtf8(retpar[0]);

      const [, expiryTimeStr] = result.split("#");

      // 获取服务器时间
      const nowTime = await getSystemTime();
      const expiryTime = moment(expiryTimeStr);
      const currentTime = moment(nowTime.data);
      /** log Ukey信息 */
      queryUserOperationLog({
        content: `ukey时间：${result}，服务器时间：${nowTime.data}`,
        operateTime: new Date().getTime(),
        operModule: "Ukey证书时间",
      });

      // 检查证书是否过期
      // if (!expiryTime.isAfter(currentTime)) {
      //   Message.error("Ukey已过期，请续期或更换其他ukey！");
      //   resolve(UKEY_STATUS.SUCCESS);
      //   return;
      // }

      // 获取子证书 CN 信息进行比对
      ukey.BaseGetCertSub(
        this.certBase64,
        1,
        this.compareUkeyInfo.bind(this, resolve, reject),
        null,
        false,
      );
    } catch (error) {
      resolve(UKEY_STATUS.SUCCESS);
    }
  }

  /**
   * 将子证书信息与当前登录信息比对
   */
  async compareUkeyInfo(resolve, reject, status, msg, retpar, params) {
    try {
      if (status !== 0) {
        // Message.error("获取CN信息失败！");
        resolve(UKEY_STATUS.SUCCESS);
        return;
      }

      // 解码证书信息
      const result = this.gbkBase64ToUtf8(retpar[0]);

      const [companyName, socialCode] = result.split("@");

      // 获取公司信息
      const data = await reqCompanyAuthDetail({ enterpriseId: this.enterpriseId });
      const { companyName: loginCompanyName, socialCode: loginSocialCode } = data?.data || {};

      /** log Ukey信息 */
      queryUserOperationLog({
        content: `ukey公司信息：${result}，登录信息：${loginCompanyName},${loginSocialCode}`,
        operateTime: new Date().getTime(),
        operModule: "UkeyCN信息",
      });
      // 比对信息
      // if (companyName === loginCompanyName && socialCode === loginSocialCode) {
      //   /** 向外抛出成功状态 */
      //   resolve(UKEY_STATUS.SUCCESS);
      //   return;
      // }

      // Message.error("Ukey信息与当前登录企业信息不匹配，请更换正确的ukey！");
      resolve(UKEY_STATUS.SUCCESS);
    } catch (error) {
      resolve(UKEY_STATUS.SUCCESS);
    }
  }

  /**
   * GBK Base64 转 UTF-8
   * @param {string} base64 - Base64 编码的字符串
   * @returns {string} - 解码后的 UTF-8 字符串
   */
  gbkBase64ToUtf8(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array([...binary].map(c => c.charCodeAt(0)));
    return new TextDecoder("gbk").decode(bytes);
  }

  /**
   * 重置状态（用于退出登录时清理）
   */
  reset() {
    this.enterpriseId = "";
    this.certBase64 = "";
  }
}

// 导出单例实例
export const ukeyValidator = new UkeyValidator();

// 保持向后兼容的导出方式
export const verifyUkeyinfo = id => ukeyValidator.verifyUkeyinfo(id);
export const getUkeyCert = (...args) => ukeyValidator.getUkeyCert(...args);
export const getUkeyInfoSub = (...args) => ukeyValidator.getUkeyInfoSub(...args);
export const getCertValidPer = (...args) => ukeyValidator.getCertValidPer(...args);
export const compareUkeyInfo = (...args) => ukeyValidator.compareUkeyInfo(...args);
export const gbkBase64ToUtf8 = base64 => ukeyValidator.gbkBase64ToUtf8(base64);
